import {
  ArrowIcon,
  AlertIcon,
  Button,
  CheckmarkIcon,
  colors,
  CompanyShopIcon,
  IconButton,
  LabeledInput,
  Link,
  MarketingShopIcon,
  PopupShopIcon,
  ShopifyShopIcon,
  SkubotSpinner,
  Text,
  PoDShopIcon,
} from "@commonsku/styles";
import React, { useState } from "react";
import styled from "styled-components";
import { shopDetails } from "./constants";
import {
  buildPoDCredentialStatusEndpoint,
  usePoDCredentialStatus,
} from "../../../hooks/usePoDCredentialStatus";
import { oauth } from "../../../utils";
import { useIdentity } from "../../../hooks";
import { trackEvent } from "../../../utils/analytics";

interface SelectedShopPageProps {
  shopType: string;
  handleCustomRouting: (newRoute: string, selectedShopType?: string) => void;
  isShopsTermsAndConditionsAccepted?: boolean;
}

const PodFormContainer = styled.div`
  margin-top: 28px;
  max-width: 840px;
`;

const PodInputRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  margin-bottom: 8px;

  > * {
    flex: 1;
  }
`;

const PodErrorRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  color: ${colors.errors.main};
`;

const PodButtonRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 48px;
  width: 100%;
  max-width: 840px;
  justify-content: flex-end;
`;

const SelectedShopPage = ({
  shopType,
  handleCustomRouting,
  isShopsTermsAndConditionsAccepted,
}: SelectedShopPageProps) => {
  const identity = useIdentity();
  const isPoDShop = "POD" === shopType;
  const {
    credentialStatus,
    isLoading: isLoadingPoDCredential,
    reload: reloadPoDCredentialStatus,
  } = usePoDCredentialStatus(isPoDShop);
  const [accountIdOverride, setAccountIdOverride] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [poDError, setPoDError] = useState<string | null>(null);
  const accountId = accountIdOverride
    ?? (isLoadingPoDCredential ? "" : (credentialStatus.account_id ?? ""));
  const isPodButtonDisabled = isPoDShop
    && (isLoadingPoDCredential
      || isValidating
      || (!credentialStatus.configured && (!accountId.trim() || !apiKey.trim()))
    );

  if ("SHOPIFY" === shopType && false === isShopsTermsAndConditionsAccepted) {
    handleCustomRouting("initialize", "SHOPIFY");
    return;
  }

  const handleCreateShopClick = () => {
    if ("POD" === shopType) {
      trackEvent("PoD - Landing", {
        tenant_id: identity.tenant_id,
        login_email: identity.login_email,
      });
      void validatePodDetails();
    } else {
      handleCustomRouting("createShopPage", shopType);
    }
  };

  const validatePodDetails = async () => {
    const trimmedAccountId = accountId.trim();
    const trimmedApiKey = apiKey.trim();
    const hasSavedCredential = credentialStatus.configured;
    const divisionId = credentialStatus.division_id;

    setPoDError(null);
    setIsValidating(true);
    try {
      if (!hasSavedCredential && (!trimmedAccountId || !trimmedApiKey)) {
        throw new Error("Fulfill Engine account ID and API key are required.");
      }

      const hasUnsavedCredential = "" !== trimmedApiKey;
      if (hasUnsavedCredential) {
        const upsertPayload: Record<string, string> = {
          account_id: trimmedAccountId,
          api_key: trimmedApiKey,
        };
        if (divisionId) {
          upsertPayload.division_id = divisionId;
        }

        await oauth("PUT", buildPoDCredentialStatusEndpoint(), upsertPayload);
        await reloadPoDCredentialStatus();
      }

      const authPayload: Record<string, string> = {};
      if (divisionId) {
        authPayload.division_id = divisionId;
      }
      if (hasUnsavedCredential) {
        authPayload.account_id = trimmedAccountId;
        authPayload.api_key = trimmedApiKey;
      }

      const response = await oauth("POST", "pod/suppliers/authentication", authPayload);
      const isPodCredentialValid = Boolean(response?.json?.authenticated);
      if (isPodCredentialValid) {
        setApiKey("");
        handleCustomRouting("createShopPage", shopType);
        return;
      }

      setPoDError("We couldn't verify your credentials.");
    } catch (error: any) {
      setPoDError(
        error?.json?.error
          || error?.message
          || "We couldn't verify your credentials.",
      );
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        width: "85%",
        margin: "auto",
        marginTop: 64,
        marginBottom: 64,
      }}
    >
      <div style={{ marginRight: 24 }}>
        {"POPUP" === shopType ? (
          <PopupShopIcon color="#E6BA00" size="huge" width={240} height={240} />
        ) : "COMPANY" === shopType ? (
          <CompanyShopIcon
            color={colors.secondary3["60"]}
            size="huge"
            width={240}
            height={240}
          />
        ) : "MARKETING" === shopType ? (
          <MarketingShopIcon
            color={colors.primary1["70"]}
            size="huge"
            width={240}
            height={240}
          />
        ) : "POD" === shopType ? (
          <PoDShopIcon
            color={colors.primary1["70"]}
            size="huge"
            width={240}
            height={240}
          />
        ) : (
          <ShopifyShopIcon
            color="#95BF47"
            size="huge"
            width={240}
            height={240}
          />
        )}
      </div>
      <div style={{ textAlign: "left" }}>
        <Text
          as="h1"
          style={{ fontWeight: 600, fontSize: 32, marginBottom: 32 }}
        >
          Your Ideal Shop Type is {shopDetails[shopType].type} Shop
        </Text>
        <Text as="p" style={{ marginBottom: 24 }}>
          {shopDetails[shopType].desc}
        </Text>
        {Object.entries(shopDetails[shopType].details).map(
          ([key, value]: [string, string], index) => (
            <div
              key={`shopDetails-${index}`}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <CheckmarkIcon color={colors.secondary3["60"]} />
              <Text
                as="span"
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  display: "block",
                  whiteSpace: "nowrap",
                }}
              >
                {key}:
              </Text>
              <Text
                as="p"
                style={{
                  fontSize: 18,
                  fontWeight: 400,
                  marginBottom: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </Text>
            </div>
          )
        )}

        {isPoDShop && !credentialStatus.configured  && (
          <PodFormContainer>
            <>
              <PodInputRow>
                <LabeledInput
                  label="Fulfill Engine Account ID"
                  style={{ marginBottom: 0 }}
                  value={accountId}
                  placeholder="Type Here"
                  onChange={(e) => {
                    setAccountIdOverride(e.target.value);
                    setPoDError(null);
                  }}
                  disabled={isLoadingPoDCredential || isValidating}
                  required
                  error={Boolean(poDError)}
                />
                <LabeledInput
                  label="Fulfill Engine API Key"
                  type="password"
                  style={{ marginBottom: 0 }}
                  value={apiKey}
                  placeholder="Type Here"
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setPoDError(null);
                  }}
                  disabled={isLoadingPoDCredential || isValidating}
                  required
                  error={Boolean(poDError)}
                />
              </PodInputRow>
              {poDError ? (
                <PodErrorRow>
                  <AlertIcon color={colors.errors.main} />
                  <Text as="p" style={{ marginBottom: 0 }}>
                    We couldn&apos;t verify your credentials. Check your{" "}
                    <Link
                      as="a"
                      href="https://www.fulfillengine.com/"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Fulfill Engine
                    </Link>{" "}
                    Account ID and API Key and try again.
                  </Text>
                </PodErrorRow>
              ) : null}
            </>
          </PodFormContainer>
        )}
        <PodButtonRow>
          <Button
            secondary
            size="large"
            onClick={() => handleCustomRouting("initialize")}
            id="shopCreation-selectedShopPage-exploreShops.btn"
          >
            Explore Other Shop Types
          </Button>
          {isPoDShop && isValidating ? (
            <Button
              size="large"
              disabled
              id="shopCreation-selectedShopPage-createShop.btn"
            >
              <SkubotSpinner size="button" skubot={false} />
            </Button>
          ) : (
            <IconButton
              size="large"
              Icon={ArrowIcon}
              iconPosition="right"
              disabled={isPodButtonDisabled}
              onClick={handleCreateShopClick}
              id="shopCreation-selectedShopPage-createShop.btn"
            >
              Create {shopDetails[shopType].type} Shop
            </IconButton>
          )}
        </PodButtonRow>
      </div>
    </div>
  );
};

export default SelectedShopPage;
